import { Autocomplete, Box, Button, Stack, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQualityControlTool } from '@/context/Qualitycontrol.context';
import { toWeightUnit } from '@/helpers/unit';
import { useCountries } from '@/hooks/use-countries';
import { useHallmarkCompositions } from '@/hooks/use-hallmark-compositions';
import { useQcMode } from '@/hooks/use-qcMode';
import { ErrorField, ErrorType } from '@/types/QcInfo';
import DecimalField from '../Common/Field/DecimalField';

interface WeightAndHallmarkProps {
  isRequired: boolean;
  errors?: ErrorType[];
}

type CountryOption = { label: string; value: string; isoCode: string };
type CompositionOption = { label: string };

const WeightAndHallmark = ({ isRequired, errors }: WeightAndHallmarkProps) => {
  const { t } = useTranslation();

  const { weight, setWeight, hallmarks, setHallmarks } =
    useQualityControlTool();

  const { isViewMode } = useQcMode();

  const { data: countries } = useCountries();
  const { data: hallmarkCompositions } = useHallmarkCompositions();

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    null
  );
  const [selectedComposition, setSelectedComposition] =
    useState<CompositionOption | null>(null);
  const [isAddDisabled, setIsAddDisabled] = useState(true);

  const weightError = useMemo(() => {
    if (weight) return undefined;
    return errors && errors.find((e) => e.field === ErrorField.WEIGHT);
  }, [errors, weight]);

  const hallmarkError = useMemo(() => {
    if (hallmarks) return undefined;
    return errors && errors.find((e) => e.field === ErrorField.HALLMARKS);
  }, [errors, hallmarks]);

  const countryOptions: CountryOption[] = useMemo(() => {
    if (!countries?.length) return [];
    return countries.map((c) => ({
      label: `${c.name} ${c.isoCode}`,
      value: c.legacyId,
      isoCode: c.isoCode,
    }));
  }, [countries]);

  const compositionOptions: CompositionOption[] = useMemo(() => {
    if (!hallmarkCompositions?.length) return [];
    return hallmarkCompositions.map((c) => ({ label: c.label }));
  }, [hallmarkCompositions]);

  const handleCountryChange = (_: unknown, option: CountryOption | null) => {
    setSelectedCountry(option);
    setIsAddDisabled(false);
  };

  const handleCompositionChange = (
    _: unknown,
    option: CompositionOption | null
  ) => {
    setSelectedComposition(option);
    setIsAddDisabled(false);
  };

  const handleAddHallmark = () => {
    const entry = [selectedCountry?.isoCode, selectedComposition?.label]
      .filter(Boolean)
      .join(' ');
    if (!entry) return;

    const current = hallmarks?.trim() ?? '';
    setHallmarks(current ? `${current}, ${entry}` : entry);
    setIsAddDisabled(true);
  };

  return (
    <Stack mt={1.25} mx={1} gap={1.5}>
      <DecimalField
        data-testid="weight_input_text"
        id="weight"
        value={weight}
        onChange={setWeight}
        label={toWeightUnit(t('QC_V2.DEFECT.WEIGHT'))}
        placeholder={t('QC_V2.MAIN.WEIGHT_PLACEHOLDER')}
        sx={{ mb: 2 }}
        required={isRequired}
        error={weightError?.field === 'weight'}
        helperText={weightError?.message}
        InputLabelProps={{
          shrink: true,
        }}
        disabled={isViewMode}
      />

      <Autocomplete
        disabled={isViewMode}
        options={countryOptions}
        value={selectedCountry}
        onChange={handleCountryChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('QC_V2.MAIN.HALLMARK_COUNTRY')}
            placeholder={t('QC_V2.MAIN.HALLMARK_COUNTRY_PLACEHOLDER')}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />

      <Autocomplete
        disabled={isViewMode}
        options={compositionOptions}
        value={selectedComposition}
        onChange={handleCompositionChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('QC_V2.MAIN.HALLMARK_COMPOSITION')}
            placeholder={t('QC_V2.MAIN.HALLMARK_COMPOSITION_PLACEHOLDER')}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />

      <Box display="flex" justifyContent="flex-end">
        <Button
          data-testid="hallmark_add_button"
          variant="outlined"
          size="small"
          onClick={handleAddHallmark}
          disabled={
            isViewMode ||
            isAddDisabled ||
            (!selectedCountry && !selectedComposition)
          }
        >
          {t('QC_V2.MAIN.ADD')}
        </Button>
      </Box>

      <TextField
        data-testid="hallmarks_input_text"
        id="hallmark"
        label={t('QC_V2.MAIN.HALLMARKS')}
        placeholder={t('QC_V2.MAIN.HALLMARK_PLACEHOLDER')}
        value={hallmarks}
        sx={{ mb: 2 }}
        required={isRequired}
        error={hallmarkError?.field === 'hallmarks'}
        helperText={hallmarkError?.message}
        InputLabelProps={{
          shrink: true,
        }}
        disabled={isViewMode}
        multiline
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setHallmarks(event.target.value);
        }}
      />
    </Stack>
  );
};

export default WeightAndHallmark;
